/**
 * Utilitários para manipulação de cookies
 * Suporte a chunked cookies para tokens grandes (>4KB)
 */

import { StorageType } from '@/types/storage.type';

/**
 * Função genérica para ler um cookie pelo nome
 */
export function getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;

    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
        let cookie = cookies[i];
        while (cookie.charAt(0) === ' ') {
            cookie = cookie.substring(1, cookie.length);
        }
        if (cookie.indexOf(nameEQ) === 0) {
            return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
        }
    }

    return null;
}

/**
 * Recupera um cookie grande reconstruindo-o a partir das partes (chunked cookies)
 * 
 * Estrutura esperada:
 * - `{name}_chunks` → número de partes (ex: "3")
 * - `{name}_0` → primeira parte do token
 * - `{name}_1` → segunda parte do token
 * - `{name}_2` → terceira parte (etc.)
 * 
 * Se não houver chunks, faz fallback para ler o cookie normal (retrocompatibilidade)
 */
export function getChunkedCookie(name: string): string | null {
    const chunksCountStr = getCookie(`${name}_chunks`);

    if (!chunksCountStr) {
        // Fallback: tentar ler como cookie normal (retrocompatibilidade)
        return getCookie(name);
    }

    const chunksCount = parseInt(chunksCountStr, 10);
    if (isNaN(chunksCount) || chunksCount <= 0) {
        return null;
    }

    let fullValue = '';
    for (let i = 0; i < chunksCount; i++) {
        const chunk = getCookie(`${name}_${i}`);
        if (chunk === null) {
            console.warn(`Cookie chunk "${name}_${i}" não encontrado`);
            return null;
        }
        fullValue += chunk;
    }

    return fullValue;
}

/**
 * Obtém o token de autenticação (suporta chunked cookies)
 */
export function getToken(): string | null {
    return getChunkedCookie(StorageType.TOKEN);
}

/**
 * Obtém o refresh token (suporta chunked cookies)
 */
export function getRefreshToken(): string | null {
    return getChunkedCookie(StorageType.REFRESH_TOKEN);
}

/**
 * Obtém o Exchange Code Token do cookie
 */
export function getExchangeCodeTokenFromCookie(): string | null {
    return getCookie(StorageType.EXCHANGE_CODE_TOKEN);
}

/**
 * Extrai a data de expiração de um token JWT
 * @param token - Token JWT
 * @returns Timestamp de expiração em segundos ou null se inválido
 */
export function getTokenExpiration(token: string): number | null {
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.exp || null;
    } catch {
        return null;
    }
}

/**
 * Verifica se um token JWT está expirado
 * @param token - Token JWT
 * @returns true se expirado ou inválido, false se válido
 */
export function isTokenExpired(token: string | null): boolean {
    if (!token) return true;
    
    const exp = getTokenExpiration(token);
    if (!exp) return true;
    
    // Adiciona margem de 60 segundos para evitar usar token prestes a expirar
    const now = Math.floor(Date.now() / 1000);
    return exp < now + 60;
}
